import React, { useState, useEffect, useRef } from 'react';
import { uploadData, getUrl, remove, list } from 'aws-amplify/storage';
import { getCurrentUser } from 'aws-amplify/auth';
import './Lyrics.css';

const Lyrics = () => {
  const [lyrics, setLyrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewLyricForm, setShowNewLyricForm] = useState(false);
  const [newLyricTitle, setNewLyricTitle] = useState('');
  const [newLyricContent, setNewLyricContent] = useState('');
  const [editingLyric, setEditingLyric] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [username, setUsername] = useState('');
  const [expandedLyrics, setExpandedLyrics] = useState({});

  useEffect(() => {
    fetchLyrics();
    fetchUsername();
  }, []);

  const fetchUsername = async () => {
    try {
      const user = await getCurrentUser();
      setUsername(user.username);
    } catch (error) {
      console.error('Error fetching username:', error);
    }
  };

  const fetchLyrics = async () => {
    try {
      setLoading(true);
      const result = await list({
        prefix: 'lyrics/'
      });

      // Process the results to get only lyric files
      const lyricFiles = result.items.filter(item => 
        item.key.startsWith('lyrics/') && 
        item.key !== 'lyrics/' && 
        !item.key.endsWith('/')
      );

      // Fetch content for each lyric file
      const lyricsWithContent = await Promise.all(
        lyricFiles.map(async (file) => {
          try {
            const urlResult = await getUrl({
              key: file.key
            });
            
            const response = await fetch(urlResult.url);
            const content = await response.text();
            
            // Extract metadata from the filename
            const filename = file.key.split('/').pop();
            const nameWithoutExtension = filename.replace('.txt', '');
            const parts = nameWithoutExtension.split('__');
            
            let title = nameWithoutExtension;
            let author = 'Unknown';
            let date = new Date().toISOString();
            
            if (parts.length >= 3) {
              title = parts[0].replace(/_/g, ' ');
              author = parts[1].replace(/_/g, ' ');
              date = new Date(parseInt(parts[2])).toISOString();
            }
            
            return {
              key: file.key,
              title,
              author,
              date,
              content,
              lastModified: file.lastModified || new Date().toISOString()
            };
          } catch (error) {
            console.error(`Error fetching content for ${file.key}:`, error);
            return {
              key: file.key,
              title: file.key.split('/').pop().replace('.txt', '').replace(/_/g, ' '),
              author: 'Unknown',
              content: 'Error loading content',
              lastModified: file.lastModified || new Date().toISOString()
            };
          }
        })
      );

      // Sort by last modified date (newest first)
      lyricsWithContent.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
      
      setLyrics(lyricsWithContent);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching lyrics:', error);
      setError('Failed to load lyrics. Please try again.');
      setLoading(false);
    }
  };

  const handleCreateLyric = async (e) => {
    e.preventDefault();
    
    if (!newLyricTitle.trim()) {
      setError('Please enter a title for your lyrics.');
      return;
    }
    
    try {
      const timestamp = Date.now();
      const formattedTitle = newLyricTitle.trim().replace(/\s+/g, '_');
      const formattedUsername = username.replace(/\s+/g, '_');
      
      // Format: Title__Author__Timestamp.txt
      const key = `lyrics/${formattedTitle}__${formattedUsername}__${timestamp}.txt`;
      
      await uploadData({
        key,
        data: newLyricContent,
        options: {
          contentType: 'text/plain'
        }
      }).result;
      
      // Reset form
      setNewLyricTitle('');
      setNewLyricContent('');
      setShowNewLyricForm(false);
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error creating lyric:', error);
      setError('Failed to create lyric. Please try again.');
    }
  };

  const handleUpdateLyric = async () => {
    if (!editingLyric) return;
    
    try {
      await uploadData({
        key: editingLyric.key,
        data: editContent,
        options: {
          contentType: 'text/plain'
        }
      }).result;
      
      // Reset editing state
      setEditingLyric(null);
      setEditContent('');
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error updating lyric:', error);
      setError('Failed to update lyric. Please try again.');
    }
  };

  const handleDeleteLyric = async (key) => {
    if (!window.confirm('Are you sure you want to delete these lyrics? This action cannot be undone.')) {
      return;
    }
    
    try {
      await remove({ key });
      
      // Refresh lyrics list
      fetchLyrics();
    } catch (error) {
      console.error('Error deleting lyric:', error);
      setError('Failed to delete lyric. Please try again.');
    }
  };

  const startEditing = (lyric) => {
    setEditingLyric(lyric);
    setEditContent(lyric.content);
  };

  const cancelEditing = () => {
    setEditingLyric(null);
    setEditContent('');
  };

  const toggleLyricExpansion = (key) => {
    setExpandedLyrics(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Reference for the printable content
  const printContentRef = useRef(null);
  
  // Function to handle printing lyrics
  const handlePrintLyrics = (lyric) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    
    // Get the current year for copyright
    const currentYear = new Date().getFullYear();
    
    // Create the print content with styling
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${lyric.title} - Night Bacon Official Lyrics</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Cormorant+Garamond:wght@500&display=swap');
          
          body {
            font-family: 'Cormorant Garamond', serif;
            line-height: 1.6;
            margin: 0;
            padding: 0;
            background-color: #fcfcfc;
            color: #333;
          }
          
          .container {
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            position: relative;
            background-color: #fff;
            box-shadow: 0 0 20px rgba(0,0,0,0.05);
            border: 1px solid #e0e0e0;
          }
          
          .letterhead {
            text-align: center;
            border-bottom: 3px double #8b0000;
            padding-bottom: 20px;
            margin-bottom: 30px;
            position: relative;
          }
          
          .logo {
            width: 80px;
            height: auto;
            margin-bottom: 10px;
          }
          
          .letterhead-title {
            font-family: 'Playfair Display', serif;
            font-size: 28px;
            color: #8b0000;
            margin: 0;
            letter-spacing: 2px;
            text-transform: uppercase;
          }
          
          .letterhead-subtitle {
            font-style: italic;
            font-size: 16px;
            color: #666;
            margin: 5px 0 0;
          }
          
          .royal-seal {
            position: absolute;
            top: 10px;
            right: 10px;
            width: 60px;
            height: 60px;
            opacity: 0.8;
          }
          
          .content {
            padding: 20px 40px;
          }
          
          h1 {
            font-family: 'Playfair Display', serif;
            font-size: 24px;
            color: #8b0000;
            margin-bottom: 5px;
            text-align: center;
          }
          
          .meta {
            font-size: 16px;
            color: #666;
            margin-bottom: 30px;
            text-align: center;
            font-style: italic;
          }
          
          .lyrics {
            white-space: pre-wrap;
            font-family: 'Cormorant Garamond', serif;
            font-size: 18px;
            line-height: 1.8;
            margin: 20px 0;
            padding: 0 20px;
          }
          
          .footer {
            margin-top: 40px;
            font-size: 14px;
            color: #666;
            text-align: center;
            border-top: 1px solid #e0e0e0;
            padding-top: 20px;
            font-style: italic;
          }
          
          .copyright {
            font-size: 12px;
            margin-top: 10px;
            color: #999;
          }
          
          .watermark {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            opacity: 0.05;
            font-size: 120px;
            color: #8b0000;
            pointer-events: none;
            z-index: 0;
            font-family: 'Playfair Display', serif;
          }
          
          @media print {
            body {
              margin: 0;
              padding: 0;
              background: none;
            }
            
            .container {
              box-shadow: none;
              border: none;
              padding: 0.5in;
            }
            
            .watermark {
              opacity: 0.03;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="watermark">NIGHT BACON</div>
          
          <div class="letterhead">
            <img src="/NightBacon.png" alt="Night Bacon Logo" class="logo" />
            <h2 class="letterhead-title">Night Bacon</h2>
            <p class="letterhead-subtitle">Official Band Lyrics</p>
            <img src="/lm.png" alt="Royal Seal" class="royal-seal" />
          </div>
          
          <div class="content">
            <h1>${lyric.title}</h1>
            <div class="meta">
              <div>Composed by: ${lyric.author}</div>
              <div>Last revised: ${formatDate(lyric.lastModified)}</div>
            </div>
            
            <div class="lyrics">${lyric.content}</div>
            
            <div class="footer">
              <p>By authority of Night Bacon Band</p>
              <p class="copyright">© ${currentYear} Night Bacon. All rights reserved. These lyrics are confidential and intended solely for band members.</p>
              <p class="copyright">Unauthorized reproduction or distribution is strictly prohibited.</p>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    // Write the content to the new window
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = function() {
      printWindow.print();
      // Close the window after print dialog closes (optional)
      // printWindow.onafterprint = function() {
      //   printWindow.close();
      // };
    };
  };

  return (
    <div className="lyrics-container">
      <h2>Band Lyrics</h2>
      
      {error && (
        <div className="error-message">
          {error}
          <button className="dismiss-button" onClick={() => setError('')}>×</button>
        </div>
      )}
      
      <div className="lyrics-actions">
        <button 
          className="new-lyric-button"
          onClick={() => setShowNewLyricForm(!showNewLyricForm)}
        >
          {showNewLyricForm ? 'Cancel' : 'Add New Lyrics'}
        </button>
      </div>
      
      {showNewLyricForm && (
        <div className="lyric-form">
          <h3>Create New Lyrics</h3>
          <form onSubmit={handleCreateLyric}>
            <div className="form-group">
              <label htmlFor="lyric-title">Song Title</label>
              <input
                id="lyric-title"
                type="text"
                value={newLyricTitle}
                onChange={(e) => setNewLyricTitle(e.target.value)}
                placeholder="Enter song title"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lyric-content">Lyrics</label>
              <textarea
                id="lyric-content"
                value={newLyricContent}
                onChange={(e) => setNewLyricContent(e.target.value)}
                placeholder="Enter lyrics here..."
                rows={10}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="save-button">Save Lyrics</button>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setShowNewLyricForm(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
      
      {loading ? (
        <div className="loading-lyrics">Loading lyrics...</div>
      ) : lyrics.length === 0 ? (
        <div className="no-lyrics">
          <p>No lyrics have been added yet.</p>
          <p>Click "Add New Lyrics" to create the first song lyrics.</p>
        </div>
      ) : (
        <div className="lyrics-list">
          {lyrics.map((lyric) => (
            <div key={lyric.key} className="lyric-card">
              {editingLyric && editingLyric.key === lyric.key ? (
                <div className="lyric-edit">
                  <h3>{lyric.title}</h3>
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={15}
                  />
                  <div className="edit-actions">
                    <button onClick={handleUpdateLyric} className="save-button">Save Changes</button>
                    <button onClick={cancelEditing} className="cancel-button">Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="lyric-header" onClick={() => toggleLyricExpansion(lyric.key)}>
                    <div className="lyric-title-row">
                      <h3>{lyric.title}</h3>
                      <span className="expand-icon">
                        {expandedLyrics[lyric.key] ? '▼' : '►'}
                      </span>
                    </div>
                    <div className="lyric-meta">
                      <span className="lyric-author">By: {lyric.author}</span>
                      <span className="lyric-date">Last updated: {formatDate(lyric.lastModified)}</span>
                    </div>
                  </div>
                  
                  {expandedLyrics[lyric.key] && (
                    <>
                      <div className="lyric-content">
                        <pre>{lyric.content}</pre>
                      </div>
                      <div className="lyric-actions">
                        <button onClick={() => startEditing(lyric)} className="edit-button">Edit</button>
                        <button onClick={() => handleDeleteLyric(lyric.key)} className="delete-button">Delete</button>
                        <button onClick={() => handlePrintLyrics(lyric)} className="print-button">Print</button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
      
      <div className="band-logo">
        <img src="/lm.png" alt="Band Logo" style={{ maxWidth: '300px', marginTop: '40px' }} />
      </div>
    </div>
  );
};

export default Lyrics;
